import { useState, useEffect } from 'react';
import { LoadingButton } from '../ui/LoadingSpinner';
import { handleApiError } from '../../utils/errorHandler';
import OmButton from '../ui/OmButton';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmTextField from '../ui/OmTextField';
import OmAlert from '../ui/OmAlert';

const StatusUpdateModal = ({ 
  show, 
  onClose, 
  newStatus, 
  setNewStatus, 
  statusComment, 
  setStatusComment, 
  getAvailableStatuses, 
  onUpdate, 
  isUpdating 
}) => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset error and success when modal opens/closes
  useEffect(() => {
    if (show) {
      setError(null);
      setSuccess(false);
    }
  }, [show]);

  // Reset error when status or comment changes
  useEffect(() => {
    if (error) setError(null);
  }, [newStatus, statusComment, error]);

  if (!show) return null;

  const handleUpdate = async () => {
    if (!newStatus) {
      setError('Please select a status');
      return;
    }

    try {
      await onUpdate();
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      // Task 3.2: Use handleApiError for consistent error handling
      const errorInfo = handleApiError(err);
      setError(errorInfo.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-carbon-black bg-opacity-60 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-0 border border-steel/20 w-96 shadow-depth bg-crystal-white">
        <div className="card-header">
          <h3 className="text-lg font-display font-semibold text-crystal-white">Update Claim Status</h3>
        </div>
        
        <div className="card-body space-y-6">
          {error && (
            <OmAlert
              alertType="error"
              message={error}
              showActions={false}
            />
          )}

          {success && (
            <OmAlert
              alertType="success"
              message="Status updated successfully!"
              showActions={false}
            />
          )}

          <div>
            <OmSelect
              label="New Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={isUpdating || success}
              required
              customValidationMessage={error && !newStatus ? "Please select a status" : ""}
              alwaysShowValidationMessage={!!error && !newStatus}
            >
              <OmSelectOption value="">Select status</OmSelectOption>
              {getAvailableStatuses().map(status => (
                <OmSelectOption key={status.value} value={status.value}>
                  {status.label}
                </OmSelectOption>
              ))}
            </OmSelect>
          </div>

          <div>
            <OmTextField
              label={`Comment ${newStatus === 'REJECTED' || newStatus === 'MISSING_DOCUMENTS' ? '(required)' : '(optional)'}`}
              value={statusComment}
              onChange={(e) => setStatusComment(e.target.value)}
              rows={4}
              placeholder="Add a comment about this status change..."
              disabled={isUpdating || success}
              required={newStatus === 'REJECTED' || newStatus === 'MISSING_DOCUMENTS'}
              customValidationMessage={
                (newStatus === 'REJECTED' || newStatus === 'MISSING_DOCUMENTS') && !statusComment.trim() 
                  ? "Comment is required for this status" 
                  : ""
              }
              alwaysShowValidationMessage={
                !!((newStatus === 'REJECTED' || newStatus === 'MISSING_DOCUMENTS') && !statusComment.trim())
              }
              supportingText={`Characters: ${statusComment.length}`}
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-steel/10">
            <OmButton
              onClick={onClose}
              buttonType="outline-button"
              buttonSize="medium"
              disabled={isUpdating || success}
            >
              Cancel
            </OmButton>
            <LoadingButton
              onClick={handleUpdate}
              isLoading={isUpdating}
              disabled={
                !newStatus || 
                isUpdating || 
                success ||
                ((newStatus === 'REJECTED' || newStatus === 'MISSING_DOCUMENTS') && !statusComment.trim())
              }
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Update Status
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;
